'use client';

import { c as _c } from "react/compiler-runtime";
import { Autocomplete, Box, Divider, FormControl, FormLabel, Stack, TextField, Typography } from '@mui/material';
import type { AzureCmkEligibleApp } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';
import { useDAL } from '@/contexts/useDAL';
import type { BackupVault } from '@/data/vaults/backupVault';
import { BottomDialog } from '../dialogs/bottomDialog';
import { FormTextField } from '../form/formTextField';
import { getCloudFieldConfig } from '../form/validation/cloudFieldValidation';
import { PanelWrapper } from '../panels/panelWrapper';
import { BrowserGrid } from '../shared/browserGrid';
import { CopyableCode } from '../shared/copyableCode';
import { ChangeableTextField } from '../text/changeableTextField';
const keyVaultKeyUriConfig = getCloudFieldConfig(Provider.Azure, 'keyVaultKeyUri');
function tenantIdListed(tenantId: string | undefined, onboardedTenantIds: readonly string[]): boolean {
  if (!tenantId?.trim()) {
    return false;
  }
  const t = tenantId.trim().toLowerCase();
  return onboardedTenantIds.some(id => id.toLowerCase() === t);
}
const EMPTY_TENANT_IDS: string[] = [];
const AzureKeyUriDescription = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Typography variant="body1" component="span">Choose the Microsoft Entra tenant where the Key Vault lives, the Eon app that will access the key (service principal in that tenant), and the Key Vault key URI. The storage account's managed identity must be granted access to the key.</Typography>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const AzureKeyVaultKeyUriEditorPanel = ({
  open,
  onClose,
  data,
  setData,
  vaultAccountId,
  defaultAzureTenantId
}: {
  open: boolean;
  onClose: () => void;
  data: BackupVault;
  setData: (data: BackupVault) => void;
  vaultAccountId: string;
  defaultAzureTenantId?: string;
}) => {
  const dal = useDAL();
  const [localData, setLocalData] = useState(data);
  const cmkTenantId = localData.vaultAttributes.azure?.cmkKeyVaultTenantId?.trim() || '';
  const {
    data: tenants,
    isLoading: loadingTenants
  } = dal.cloudAccounts.azureApps.useListTenants();
  const {
    data: eligibleApps,
    isLoading: loadingApps
  } = dal.vaults.useListAzureCmkEligibleApps(vaultAccountId, cmkTenantId);
  const onboardedTenantIds = tenants?.tenantIds ?? EMPTY_TENANT_IDS;
  useEffect(() => {
    if (!open || loadingTenants) {
      return;
    }
    const fromForm = data.vaultAttributes.azure?.cmkKeyVaultTenantId?.trim() ?? '';
    const defaultIfListed = defaultAzureTenantId && tenantIdListed(defaultAzureTenantId, onboardedTenantIds) ? defaultAzureTenantId.trim() : '';
    setLocalData(prev => {
      const newTenant = fromForm || defaultIfListed;
      const tenantChanged = newTenant.toLowerCase() !== (prev.vaultAttributes.azure?.cmkKeyVaultTenantId?.trim() || '').toLowerCase();
      return {
        ...prev,
        vaultAttributes: {
          ...prev.vaultAttributes,
          azure: {
            ...prev.vaultAttributes.azure,
            cmkKeyVaultTenantId: newTenant,
            ...(tenantChanged ? {
              cmkAppRegistrationId: ''
            } : {})
          }
        }
      };
    });
  }, [open, data, defaultAzureTenantId, loadingTenants, onboardedTenantIds]);
  const tenantOptions = useMemo(() => {
    const base = [...onboardedTenantIds];
    if (cmkTenantId && !base.some(id => id.toLowerCase() === cmkTenantId.toLowerCase())) {
      base.push(cmkTenantId);
    }
    return base;
  }, [onboardedTenantIds, cmkTenantId]);
  const appOptions = eligibleApps?.apps ?? [];
  const selectedApp: AzureCmkEligibleApp | null = appOptions.find(a => a.appRegistrationId === localData.vaultAttributes.azure?.cmkAppRegistrationId) ?? null;
  const keyUri = localData.vaultAttributes.azure?.encryptionKey?.trim() ?? '';
  const keyVaultName = /^https:\/\/([a-z0-9-]+)\.vault\.azure\.net\//i.exec(keyUri)?.[1];
  const spId = selectedApp?.servicePrincipalId ?? '';
  const canShowSetupScript = !!(cmkTenantId && spId && keyVaultName);
  const keyVaultSetupScript = `\
# Log in to the tenant that owns the Key Vault
az login --tenant "${cmkTenantId || '<tenant-id>'}"

# Azure rejects customer-managed encryption unless the Key Vault is recoverable
softDelete=$(az keyvault show --name "${keyVaultName ?? '<vault-name>'}" --query "properties.enableSoftDelete" -o tsv)
purgeProtection=$(az keyvault show --name "${keyVaultName ?? '<vault-name>'}" --query "properties.enablePurgeProtection" -o tsv)

if [ "$softDelete" = "true" ] && [ "$purgeProtection" = "true" ]; then
  # Assign "Key Vault Crypto Service Encryption User" to the Eon app's service principal
  az role assignment create \\
    --assignee-object-id "${spId || '<service-principal-id>'}" \\
    --assignee-principal-type ServicePrincipal \\
    --role "e147488a-f6f5-4113-8e2d-b22465e65bf6" \\
    --scope "$(az keyvault show --name "${keyVaultName ?? '<vault-name>'}" --query id -o tsv)"
else
  echo "Key Vault is not recoverable: soft delete=\${softDelete:-false}, purge protection=\${purgeProtection:-false}"
  echo "No role was assigned. Enable both and re-run; purge protection cannot be disabled once enabled:"
  echo "  az keyvault update --name ${keyVaultName ?? '<vault-name>'} --enable-purge-protection true"
  false # non-zero status so a caller wrapping this script sees the setup did not complete
fi`;
  return <BottomDialog open={open}>
      <PanelWrapper header={{
      title: 'Customer-managed Key Vault key',
      subtitle: <AzureKeyUriDescription />,
      fullWidthSubtitle: false,
      onClose
    }} footer={{
      primaryButton: {
        text: 'Done',
        'data-testid': 'azure-key-uri-done-button',
        onClick: () => {
          setData(localData);
          onClose();
        }
      },
      secondaryButton: {
        text: 'Cancel',
        'data-testid': 'azure-key-uri-cancel-button',
        onClick: onClose
      }
    }}>
        <Box sx={{
        padding: '40px min(100px,6vw)',
        width: '100%',
        height: 'auto'
      }}>
          <BrowserGrid columns={2} gap='40px'>
            <Stack divider={<Divider sx={{
            borderStyle: 'dashed'
          }} />} sx={{
            gap: '40px'
          }}>
              <FormControl>
                <FormLabel>Key Vault tenant</FormLabel>
                <Autocomplete loading={loadingTenants} value={cmkTenantId || null} onChange={(_, value) => {
                setLocalData({
                  ...localData,
                  vaultAttributes: {
                    ...localData.vaultAttributes,
                    azure: {
                      ...localData.vaultAttributes.azure,
                      cmkKeyVaultTenantId: value ?? '',
                      cmkAppRegistrationId: ''
                    }
                  }
                });
              }} options={tenantOptions} renderInput={params => <TextField {...params} placeholder='Select tenant' label='' slotProps={{
                ...params.slotProps,
                inputLabel: {
                  shrink: true
                }
              }} />} />
              </FormControl>
              <FormControl>
                <FormLabel>App for Key Vault access</FormLabel>
                <Autocomplete loading={loadingApps} value={selectedApp} onChange={(__0, app) => {
                setLocalData({
                  ...localData,
                  vaultAttributes: {
                    ...localData.vaultAttributes,
                    azure: {
                      ...localData.vaultAttributes.azure,
                      cmkAppRegistrationId: app?.appRegistrationId ?? ''
                    }
                  }
                });
              }} options={appOptions} isOptionEqualToValue={(a_0, b) => a_0.appRegistrationId === b.appRegistrationId} getOptionLabel={app_0 => `App ID: ${app_0.applicationClientId}${app_0.servicePrincipalId ? ` | SP ID: ${app_0.servicePrincipalId}` : ''}`} disabled={!cmkTenantId} renderInput={params_0 => <TextField {...params_0} placeholder={cmkTenantId ? 'Select app' : 'Select a Key Vault tenant first'} label='' slotProps={{
                ...params_0.slotProps,
                inputLabel: {
                  shrink: true
                }
              }} />} />
              </FormControl>
              <FormTextField title='Key Vault key URI (versionless)' alwaysShowHelp helpText={<>
                    Paste the key URI <strong>without</strong> a version segment
                    — e.g.{' '}
                    <code>https://my-vault.vault.azure.net/keys/my-key</code>,{' '}
                    <strong>not</strong>{' '}
                    <code>.../keys/my-key/&lt;version-id&gt;</code>. Versionless
                    URIs let Azure Storage automatically pick up new key
                    versions and re-wrap the account encryption key within ~1
                    hour, so rotation works without any manual update from you.
                  </>} placeholder={keyVaultKeyUriConfig.placeholder} value={localData.vaultAttributes.azure?.encryptionKey ?? ''} onChange={event => {
              setLocalData({
                ...localData,
                vaultAttributes: {
                  ...localData.vaultAttributes,
                  azure: {
                    ...localData.vaultAttributes.azure,
                    encryptionKey: event.target.value
                  }
                }
              });
            }} validation={keyVaultKeyUriConfig.validation} />
            </Stack>
            <Stack sx={{
            gap: '12px'
          }}>
              <Typography variant='subtitle1'>Prepare the Key Vault</Typography>
              <Typography variant='body2'>
                The Key Vault must have <strong>soft delete</strong> and{' '}
                <strong>purge protection</strong> enabled — Azure refuses
                customer-managed encryption on a Key Vault whose key could be
                permanently purged, which would leave the vault&apos;s data
                unrecoverable. The selected app&apos;s service principal must
                also have the{' '}
                <strong>Key Vault Crypto Service Encryption User</strong> role
                on it. Run the following script in your environment:
              </Typography>
              <CopyableCode title='Bash' code={keyVaultSetupScript} blur={!canShowSetupScript} />
            </Stack>
          </BrowserGrid>
        </Box>
      </PanelWrapper>
    </BottomDialog>;
};
export const AzureKeyVaultKeyUriTextField = t0 => {
  const $ = _c(14);
  const {
    backupVault,
    setBackupVault,
    vaultAccountId,
    defaultAzureTenantId
  } = t0;
  const [openEditor, setOpenEditor] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const encryptionKey = backupVault.vaultAttributes.azure?.encryptionKey;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setEditorKey(_temp);
      setOpenEditor(true);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== encryptionKey) {
    t2 = <ChangeableTextField value={encryptionKey} onClick={t1} placeholder="No key URI set" noValueButtonText="Set key URI" />;
    $[1] = encryptionKey;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setOpenEditor(false);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== backupVault || $[5] !== defaultAzureTenantId || $[6] !== editorKey || $[7] !== openEditor || $[8] !== setBackupVault || $[9] !== vaultAccountId) {
    t4 = <AzureKeyVaultKeyUriEditorPanel key={editorKey} open={openEditor} onClose={t3} data={backupVault} setData={setBackupVault} vaultAccountId={vaultAccountId} defaultAzureTenantId={defaultAzureTenantId} />;
    $[4] = backupVault;
    $[5] = defaultAzureTenantId;
    $[6] = editorKey;
    $[7] = openEditor;
    $[8] = setBackupVault;
    $[9] = vaultAccountId;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t4) {
    t5 = <>{t2}{t4}</>;
    $[11] = t2;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
function _temp(k) {
  return k + 1;
}